import type { ReactNode } from 'react';

/**
 * Just the label line. Use this when a field's control and hint are already laid
 * out (a grid cell, an inline row) and only the label styling needs to be shared;
 * use FormField below when you want the whole label / control / hint stack.
 */
export const FieldLabel = ({ children, required }: { children: ReactNode; required?: boolean }) => (
  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
    {children}{required && <span className="ml-1 text-red-500">*</span>}
  </label>
);

/**
 * Labelled form field: the label / control / hint-or-error stack that every
 * dialog in this app was repeating inline. Keeping it in one place means a
 * required marker or an error colour changes once rather than per screen.
 */
export interface FormFieldProps {
  label: string;
  /** renders the * and is what a caller checks before submitting */
  required?: boolean;
  /** shown in red under the control; takes precedence over `hint` */
  error?: string;
  /** shown in grey under the control when there is no error */
  hint?: ReactNode;
  /**
   * Explanation shown on hover of an (i) beside the label, instead of as
   * permanent grey text under the control. Use it where the field genuinely
   * needs explaining but a standing sentence would clutter a dense grid.
   */
  info?: string;
  className?: string;
  children: ReactNode;
}

const FormField = ({ label, required, error, hint, info, className, children }: FormFieldProps) => (
  <div className={className}>
    <label className="mb-1 flex items-center gap-1 text-sm font-medium text-slate-700 dark:text-slate-300">
      <span>{label}{required && <span className="ml-1 text-red-500">*</span>}</span>
      {info && (
        <i className="pi pi-info-circle cursor-help text-[11px] text-gray-400 hover:text-primary"
          title={info} aria-label={info} />
      )}
    </label>
    {children}
    {error
      ? <small className="mt-0.5 block text-rose-600 dark:text-rose-400">{error}</small>
      : hint
        ? <small className="mt-0.5 block text-ink-muted">{hint}</small>
        : null}
  </div>
);

export default FormField;
